import { AppEventNames } from "@bezoned/shared/types/app-event-names.js";
import { Duration } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FullScreenLoader } from "../../components/ui/loaders/full-screen-loader.js";
import {
  consumeInteractiveUserConsent,
  markInteractiveUserConsentPending,
  trackAuthenticatedOnboardingEvent,
  trackPublicOnboardingEvent,
} from "../onboarding/onboarding-tracker.js";
import { usePlatform } from "../platform-detection/use-platform.js";
import { useAuthBridgeStore } from "./auth-bridge.store.js";
import { AuthContext } from "./auth.context.js";
import type { AuthContextType, UserInfo } from "./auth.context.js";
import { decodeToken } from "./token-utils.js";
import { useAuthService } from "./use-auth-service.js";

const normalizeUserId = (id: string | undefined | null) =>
  id?.split(".")[0] ?? "";

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

const isExplicitConsentDenial = (error: unknown) => {
  const message = toError(error).message.toLowerCase();
  return /cancel|denied|declined|user_cancelled|user_canceled/u.test(message);
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { authService } = useAuthService();
  const { notifyAppLoaded } = usePlatform();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // ? The auth bridge keeps auth data available to non-React consumers such as API helpers.
  const { _setAuthBridgeUser, _setAuthBridgeTokenFunction } =
    useAuthBridgeStore();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const refreshTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const isLoggingInRef = useRef(false);

  const REFRESH_EARLY_MS = Duration.fromObject({ minutes: 1 }).as(
    "milliseconds"
  );
  const PERIODIC_CHECK_MS = Duration.fromObject({ minutes: 5 }).as(
    "milliseconds"
  );
  const MIN_REFRESH_DELAY_MS = Duration.fromObject({ seconds: 5 }).as(
    "milliseconds"
  );

  const scheduleRefresh = useCallback(
    (expiresAtMs: number) => {
      if (!expiresAtMs) {
        return;
      }

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const delay = Math.max(
        MIN_REFRESH_DELAY_MS,
        expiresAtMs - Date.now() - REFRESH_EARLY_MS
      );
      refreshTimerRef.current = setTimeout(async () => {
        const token = await authService?.getToken();
        if (!token) {
          return;
        }

        setAccessToken(token);
        if (authService?.expiresAt) {
          scheduleRefresh(authService.expiresAt);
        }
      }, delay);
    },
    [authService]
  );

  const ensureAuthenticated = useCallback(async () => {
    if (!authService) {
      return null;
    }

    let token: string | null;
    try {
      token = await authService.getToken();
    } catch (error) {
      consumeInteractiveUserConsent();
      trackPublicOnboardingEvent(
        isExplicitConsentDenial(error)
          ? AppEventNames.ConsentUserDenied
          : AppEventNames.ConsentUserError
      );
      throw error;
    }
    if (!token) {
      // interactive path - will redirect
      if (typeof authService.login === "function" && !isLoggingInRef.current) {
        isLoggingInRef.current = true;
        await markInteractiveUserConsentPending();
        try {
          await authService.login();
        } catch (error) {
          consumeInteractiveUserConsent();
          trackPublicOnboardingEvent(
            isExplicitConsentDenial(error)
              ? AppEventNames.ConsentUserDenied
              : AppEventNames.ConsentUserError
          );
          throw error;
        }
      }
      return null;
    }
    setAccessToken(token);

    if (authService.expiresAt) {
      scheduleRefresh(authService.expiresAt);
    }

    return token;
  }, [authService, scheduleRefresh]);

  const handleAuthenticatedToken = useCallback(
    (token: string) => {
      const decodedToken = decodeToken(token);
      const email =
        decodedToken.email ||
        decodedToken.upn ||
        decodedToken.preferred_username;
      const tenantId = decodedToken.tid;

      const userInfo: UserInfo = {
        email: email ?? "",
        id: normalizeUserId(decodedToken.oid || decodedToken.sub),
        name:
          decodedToken.name ||
          `${decodedToken.given_name} ${decodedToken.family_name}`.trim(),
        tenantId: tenantId ?? "",
      };

      if (!(userInfo.tenantId && userInfo.id)) {
        throw new Error("Unable to resolve authenticated user claims.");
      }

      trackAuthenticatedOnboardingEvent(
        consumeInteractiveUserConsent()
          ? AppEventNames.ConsentUserSucceeded
          : AppEventNames.ConsentUserAlreadyGranted
      );

      setUser(userInfo);
      _setAuthBridgeUser(userInfo);
      if (authService) {
        _setAuthBridgeTokenFunction(authService.getToken.bind(authService));
      }

      // Notify MS Teams that the app is fully loaded and ready
      notifyAppLoaded();
    },
    [
      authService,
      _setAuthBridgeTokenFunction,
      _setAuthBridgeUser,
      notifyAppLoaded,
    ]
  );

  useEffect(() => {
    let mounted = true;
    if (!authService) {
      return;
    }

    // ? Browser throttling can pause timers while the app is hidden, so refresh again on resume.
    const refreshTokenAfterInactivity = async () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const token = await authService.getToken();
      if (!token) {
        return;
      }

      setAccessToken(token);
      if (authService.expiresAt) {
        scheduleRefresh(authService.expiresAt);
      }
    };

    const init = async () => {
      try {
        const token = await ensureAuthenticated();
        if (!(token && mounted)) {
          return;
        }

        handleAuthenticatedToken(token);
        setIsLoading(false);
      } catch (error) {
        if (mounted) {
          setAuthError(toError(error));
          setIsLoading(false);
        }
      }
    };

    init();
    document.addEventListener("visibilitychange", refreshTokenAfterInactivity);

    const periodic = setInterval(async () => {
      const token = await authService.getToken();
      if (!token) {
        return;
      }

      setAccessToken(token);
      if (authService.expiresAt) {
        scheduleRefresh(authService.expiresAt);
      }
    }, PERIODIC_CHECK_MS);

    return () => {
      mounted = false;
      clearInterval(periodic);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      document.removeEventListener(
        "visibilitychange",
        refreshTokenAfterInactivity
      );

      _setAuthBridgeTokenFunction(null);
    };
  }, [
    authService,
    ensureAuthenticated,
    handleAuthenticatedToken,
    scheduleRefresh,
    _setAuthBridgeTokenFunction,
  ]);

  const providerName = authService?.getName() ?? null;
  const hasAuthenticatedSession = Boolean(user && accessToken && providerName);

  const value = useMemo<AuthContextType>(
    () => ({
      __ctx_init: hasAuthenticatedSession,
      accessToken,
      providerName,
      user,
    }),
    [accessToken, hasAuthenticatedSession, providerName, user]
  );

  if (authError) {
    throw authError;
  }

  if (isLoading) {
    return <FullScreenLoader message="Authenticating you" />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
